// Shared shapes and display helpers for the admin audit screens.
//
// Index and Show render the same status and the same dates, so the mapping
// lives here rather than being written twice and drifting.
import * as React from "react"
import { Check, Copy } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

import type { NavTab } from "@/components/ui/nav-tabs"

// The Audits section's own sub-navigation. Defined once so the two pages cannot
// disagree about what the section contains.
export const QUESTIONS_PATH = "/admin/audits/questions"

export const AUDIT_TABS: NavTab[] = [
  // Matches /admin/audits and its child pages (an audit's own show page), but
  // not the sibling tab below. The query string is stripped first: the
  // questions page carries a ?type= for its own sub-tabs, and without this the
  // path comparison would never match it and BOTH tabs would read as active.
  {
    href: "/admin/audits",
    label: "All audits",
    match: (url) => {
      const path = url.split("?")[0]
      return path.startsWith("/admin/audits") && path !== QUESTIONS_PATH
    },
  },
  {
    href: QUESTIONS_PATH,
    label: "Intake questions",
    match: (url) => url.split("?")[0] === QUESTIONS_PATH,
  },
]

export type AuditStatus =
  | "not_opened"
  | "opened"
  | "expired"
  | "revoked"
  | "deck_deleted"

export type AuditSummary = {
  id: number
  client_name: string
  kind: string | null
  status: AuditStatus
  url: string
  version_count: number
  slide_count: number | null
  expires_at: string
  created_at: string
  opens: number
  last_seen_at: string | null
}

export type AuditSlideView = {
  sequence: number
  slide_index: number
  slide_title: string | null
  dwell_ms: number
}

// One visit, not one person: viewer_label tracks a device id, so the same human
// on a phone and a laptop reads as two viewers. The UI says "viewer" everywhere
// for that reason.
export type AuditSession = {
  id: number
  started_at: string
  last_seen_at: string
  active_ms: number
  furthest_slide: number | null
  device_type: string | null
  referrer: string | null
  version_number: number | null
  viewer_label: number
  returning: boolean
  slide_views: AuditSlideView[]
}

export type AuditDetail = AuditSummary & {
  token: string
  revoked_at: string | null
  deck_deleted_at: string | null
  first_opened_at: string | null
  expires_on: string
}

export type AuditVersion = {
  id: number
  number: number
  slide_count: number
  slide_titles: string[]
  byte_size: number
  created_at: string
}

// "Sent Tuesday, still unopened" and "opened again yesterday" are the two states
// that change what Matt does next, so opened reads as the positive tone and
// everything that has stopped working reads as danger.
const STATUS_DISPLAY: Record<
  AuditStatus,
  { label: string; tone: "accent" | "muted" | "neutral" | "danger"; title: string }
> = {
  opened: { label: "Opened", tone: "accent", title: "A real viewer has opened this deck" },
  not_opened: { label: "Not opened", tone: "muted", title: "Live, but nobody has opened it yet" },
  expired: { label: "Expired", tone: "neutral", title: "Past its expiry date — the link shows the dead-link page" },
  revoked: { label: "Revoked", tone: "danger", title: "Turned off by hand — the link shows the dead-link page" },
  deck_deleted: { label: "Deck deleted", tone: "danger", title: "The deck HTML was purged; the record and history are kept" },
}

export function AuditStatusBadge({ status }: { status: AuditStatus }) {
  const display = STATUS_DISPLAY[status]
  if (!display) return null
  return (
    <Badge tone={display.tone} title={display.title}>
      {display.label}
    </Badge>
  )
}

export const KIND_LABELS: Record<string, string> = {
  viability: "Viability",
  blind_spot: "Blind spot",
  combined: "Combined",
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

// Hand-parsed rather than via Date, so server and client render the same string
// during SSR hydration (the leads index does the same for the same reason).
export function formatDate(iso: string) {
  const [date] = iso.split("T")
  const [y, m, d] = date.split("-").map(Number)
  return `${d} ${MONTHS[m - 1]} ${y}`
}

// The link is the deliverable, so copying it is the action these pages exist for.
export function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = React.useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be refused (insecure origin, denied permission).
      // The URL is on screen either way, so there is nothing to recover.
    }
  }

  return (
    <Button variant="ghost" size="sm" onClick={copy} title={url}>
      {copied ? (
        <>
          <Check aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
          Copied
        </>
      ) : (
        <>
          <Copy aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
          Copy link
        </>
      )}
    </Button>
  )
}

// Hand-parsed for the same reason as formatDate: identical output on server and
// client so hydration doesn't mismatch.
export function formatDateTime(iso: string) {
  const [date, rest] = iso.split("T")
  const [y, m, d] = date.split("-").map(Number)
  const [hh, mm] = (rest ?? "").split(":")
  return `${d} ${MONTHS[m - 1]} ${y}, ${hh}:${mm}`
}

// Reading time, so seconds matter under a minute and hours are a tab left open.
export function formatDuration(ms: number) {
  const total = Math.round(ms / 1000)
  const seconds = total % 60
  const minutes = Math.floor(total / 60) % 60
  const hours = Math.floor(total / 3600)
  if (hours) return `${hours}h ${minutes}m`
  if (minutes) return `${minutes}m ${String(seconds).padStart(2, "0")}s`
  return `${seconds}s`
}

// The path rows are dense and read as a column, so they get the clock form
// rather than the prose one.
export function formatClock(ms: number) {
  const total = Math.round(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  const units = ["KB", "MB"]
  let size = bytes / 1024
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size < 10 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`
}
